"use client";

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import BudgetItemsModal from "./ChangeOrderBudgetItemsModal";
import DatabaseItemsModal from "./ChangeOrderDatabaseItemsModal";
import {
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowUpTrayIcon,
  PencilSquareIcon,
} from "@heroicons/react/20/solid";
import {
  XMarkIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";

const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";

// ─── Shared helpers (same as EditChangeOrderFormV3) ───────────────────────────

const formatCurrency = (num) =>
  Number.isFinite(num)
    ? `$${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "$0.00";

const isSFUnit = (uom) => {
  const u = (uom || "").toLowerCase();
  return (
    u === "sf" || u.includes("square") || u.includes("sq ft") || u === "sheet"
  );
};
const isLFUnit = (uom) => {
  const u = (uom || "").toLowerCase();
  return u === "lf" || u.includes("linear") || u.includes("lin ft");
};
const parseSizeToCoverage = (sizeStr, uom) => {
  if (!sizeStr) return null;
  const str = String(sizeStr).trim().toLowerCase();
  const parseLength = (part) => {
    if (!part) return 0;
    const mixed = part.match(/^(\d+(?:\.\d+)?)' ?(\d+(?:\.\d+)?)"?$/);
    if (mixed) return parseFloat(mixed[1]) + parseFloat(mixed[2]) / 12;
    const feet = part.match(/^([\d.]+)'?$/);
    if (feet) return parseFloat(feet[1]) || 0;
    const inch = part.match(/^([\d.]+)"$/);
    if (inch) return (parseFloat(inch[1]) || 0) / 12;
    const n = parseFloat(part);
    return isNaN(n) ? 0 : n;
  };
  if (isSFUnit(uom)) {
    const parts = str.split(/\s*[xX×]\s*/);
    if (parts.length === 2) {
      const w = parseLength(parts[0]);
      const h = parseLength(parts[1]);
      if (w > 0 && h > 0) return w * h;
    }
    const area = parseLength(parts[0]);
    return area > 0 ? area : null;
  }
  if (isLFUnit(uom)) {
    const length = parseLength(str);
    return length > 0 ? length : null;
  }
  return null;
};

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#4b5563",
    borderColor: state.isFocused ? "#f97316" : "#6b7280",
    color: "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 1px #f97316" : "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "32px",
    fontSize: "12px",
  }),
  input: (p) => ({ ...p, color: "#e5e7eb" }),
  singleValue: (p) => ({ ...p, fontSize: "12px", color: "#e5e7eb" }),
  option: (p, state) => ({
    ...p,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#6b7280" },
    fontSize: "12px",
  }),
  menu: (p) => ({
    ...p,
    backgroundColor: "#374151",
    border: "1px solid #4b5563",
    zIndex: 9999,
  }),
  menuPortal: (p) => ({ ...p, zIndex: 999999 }),
  placeholder: (p) => ({ ...p, color: "#9ca3af" }),
};

// ─── Confirmation Modal ───────────────────────────────────────────────────────
const ConfirmModal = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  confirmColor = "orange",
}) => (
  <Dialog open={open} onClose={onClose} className="relative z-50">
    <DialogBackdrop className="fixed inset-0 bg-gray-900/75" />
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <DialogPanel className="bg-gray-700 rounded-lg px-6 py-5 shadow-xl max-w-md w-full border border-gray-600">
        <div className="flex items-start gap-4">
          <ExclamationTriangleIcon className="h-6 w-6 text-orange-400 mt-0.5 shrink-0" />
          <div className="flex-1">
            <DialogTitle className="text-lg font-semibold text-orange-400">
              {title}
            </DialogTitle>
            <p className="mt-2 text-sm text-gray-200 whitespace-pre-wrap">
              {message}
            </p>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-600 text-gray-200 rounded hover:bg-gray-500"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm text-white rounded ${
              confirmColor === "green"
                ? "bg-green-600 hover:bg-green-700"
                : confirmColor === "red"
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-orange-500 hover:bg-orange-600"
            }`}
          >
            {confirmText}
          </button>
        </div>
      </DialogPanel>
    </div>
  </Dialog>
);

// ─── Amendment Item Table (mirrors CO form item table) ────────────────────────
function AmendmentItemTable({
  items = [],
  onUpdate,
  onRemove,
  onMove,
  isLocked,
  budgetItems = [],
  netQuantities,
  coItems = [],
  netLoading = false, // ← add this
}) {
  const [filters, setFilters] = useState({
    description: "",
    category: [],
    condition: [],
    bidArea: [],
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [showFilter, setShowFilter] = useState(null);
  const filterRefs = {
    category: useRef(null),
    condition: useRef(null),
    bidArea: useRef(null),
  };

  // Click-outside for filter dropdowns
  useEffect(() => {
    const handler = (e) => {
      if (!showFilter) return;
      const ref = filterRefs[showFilter];
      if (ref?.current && !ref.current.contains(e.target)) {
        if (
          e.target.closest('[class*="-menu"]') ||
          e.target.closest('[id^="react-select"]')
        )
          return;
        setShowFilter(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showFilter]);

  const requestSort = (key) =>
    setSortConfig((c) => {
      if (c.key === key) {
        if (c.direction === "asc") return { key, direction: "desc" };
        if (c.direction === "desc") return { key: null, direction: null };
      }
      return { key, direction: "asc" };
    });

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? " ↑" : " ↓";
  };

  // Assembly & bid area options from existing items + budget
  const assemblyOptions = useMemo(() => {
    const unique = new Set();
    [...budgetItems, ...items].forEach((i) => {
      if (i.conditionName) unique.add(i.conditionName);
    });
    return [
      { value: "", label: "— Select Assembly —" },
      ...Array.from(unique)
        .sort()
        .map((v) => ({ value: v, label: v })),
    ];
  }, [budgetItems, items]);

  const bidAreaOptions = useMemo(() => {
    const unique = new Set();
    [...budgetItems, ...items].forEach((i) => {
      if (i.bidArea) unique.add(i.bidArea);
    });
    return [
      { value: "", label: "— Select Bid Area —" },
      ...Array.from(unique)
        .sort()
        .map((v) => ({ value: v, label: v })),
    ];
  }, [budgetItems, items]);

  const normalizeCondition = (name) => {
    if (!name) return name;
    const match = String(name)
      .trim()
      .match(/^\d+\s*[-–—]\s*(.+)$/);
    return match ? match[1].trim() : String(name).trim();
  };

  // Filter options
  const getFilterOpts = useCallback(
    (field) => {
      const unique = new Set();
      items.forEach((item) => {
        let v;
        if (field === "category") v = item.category;
        if (field === "condition") v = normalizeCondition(item.conditionName);
        if (field === "bidArea") v = item.bidArea;
        if (v) unique.add(v);
      });
      return Array.from(unique)
        .sort()
        .map((v) => ({ value: v, label: v }));
    },
    [items],
  );

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (filters.category.length && !filters.category.includes(item.category))
        return false;
      if (
        filters.condition.length &&
        !filters.condition.includes(normalizeCondition(item.conditionName))
      )
        return false;
      if (filters.bidArea.length && !filters.bidArea.includes(item.bidArea))
        return false;
      if (filters.description) {
        const desc = (
          item.customerDescription ||
          item.itemDescription ||
          ""
        ).toLowerCase();
        if (!desc.includes(filters.description.toLowerCase())) return false;
      }
      return true;
    });
  }, [items, filters]);

  const sortedItems = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return filteredItems;
    return [...filteredItems].sort((a, b) => {
      let av, bv;
      switch (sortConfig.key) {
        case "description":
          av = (a.customerDescription || a.itemDescription || "").toLowerCase();
          bv = (b.customerDescription || b.itemDescription || "").toLowerCase();
          break;
        case "category":
          av = a.category || "";
          bv = b.category || "";
          break;
        case "conditionName":
          av = (a.conditionName || "").toLowerCase();
          bv = (b.conditionName || "").toLowerCase();
          break;
        case "bidArea":
          av = (a.bidArea || "").toLowerCase();
          bv = (b.bidArea || "").toLowerCase();
          break;
        case "deltaQty":
          av = Number(a.deltaQty ?? 0);
          bv = Number(b.deltaQty ?? 0);
          break;
        case "amount":
          av = Number(a.amount ?? 0);
          bv = Number(b.amount ?? 0);
          break;
        default:
          return 0;
      }
      if (av < bv) return sortConfig.direction === "asc" ? -1 : 1;
      if (av > bv) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredItems, sortConfig]);

  const hasFilters =
    filters.description ||
    filters.category.length ||
    filters.condition.length ||
    filters.bidArea.length ||
    sortConfig.key;

  const totalCost = useMemo(
    () => items.reduce((sum, i) => sum + (i.amount || 0), 0),
    [items],
  );

  const handleUpdateItem = (localId, field, value) => {
    onUpdate(localId, field, value);
  };

  if (items.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400 border-2 border-dashed border-gray-600 rounded-lg">
        No items yet. Add items using the buttons above.
      </div>
    );
  }

  return (
    <div>
      {/* Filter bar */}
      <div className="bg-gray-800 rounded-lg p-3 mb-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-white">
              {filteredItems.length} item{filteredItems.length !== 1 ? "s" : ""}
            </span>
            <span className="text-gray-600">|</span>
            <span
              className={`text-sm font-semibold ${totalCost >= 0 ? "text-green-400" : "text-red-400"}`}
            >
              {formatCurrency(totalCost)}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            value={filters.description}
            onChange={(e) =>
              setFilters((p) => ({ ...p, description: e.target.value }))
            }
            placeholder="Search description..."
            className="w-56 px-2 py-1 text-sm bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-500"
          />
          {/* Active filter chips */}
          {filters.category.length > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-teal-600/80 text-white rounded">
              Cat:{" "}
              {filters.category.length === 1
                ? filters.category[0]
                : `${filters.category.length}x`}
              <button
                onClick={() => setFilters((p) => ({ ...p, category: [] }))}
                className="hover:text-teal-200"
              >
                ×
              </button>
            </span>
          )}
          {filters.condition.length > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-purple-600/80 text-white rounded">
              Assembly:{" "}
              {filters.condition.length === 1
                ? filters.condition[0].substring(0, 20)
                : `${filters.condition.length}x`}
              <button
                onClick={() => setFilters((p) => ({ ...p, condition: [] }))}
                className="hover:text-purple-200"
              >
                ×
              </button>
            </span>
          )}
          {filters.bidArea.length > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-green-600/80 text-white rounded">
              Bid Area:{" "}
              {filters.bidArea.length === 1
                ? filters.bidArea[0]
                : `${filters.bidArea.length}x`}
              <button
                onClick={() => setFilters((p) => ({ ...p, bidArea: [] }))}
                className="hover:text-green-200"
              >
                ×
              </button>
            </span>
          )}
          {(filters.description ||
            filters.category.length ||
            filters.condition.length ||
            filters.bidArea.length) && (
            <button
              onClick={() =>
                setFilters({
                  description: "",
                  category: [],
                  condition: [],
                  bidArea: [],
                })
              }
              className="text-sm text-red-400 hover:text-red-300"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-600 text-xs">
          <thead className="bg-gray-800 sticky top-0 z-10">
            <tr>
              <th
                className="px-2 py-2 text-left text-gray-300 uppercase tracking-wider cursor-pointer min-w-[180px]"
                onClick={() => requestSort("description")}
              >
                Description
                {getSortIndicator("description") && (
                  <span className="ml-1 text-orange-400">
                    {getSortIndicator("description")}
                  </span>
                )}
              </th>

              {/* Category - filterable */}
              <th
                className={`px-2 py-2 text-center uppercase tracking-wider cursor-pointer relative ${filters.category.length ? "text-orange-400" : "text-gray-300"}`}
                onClick={(e) => {
                  if (!e.target.closest('[class*="css-"]'))
                    setShowFilter(
                      showFilter === "category" ? null : "category",
                    );
                }}
              >
                <span className="flex items-center justify-center gap-0.5">
                  Cat <ChevronDownIcon className="w-3 h-3" />
                  {filters.category.length > 0 && (
                    <span className="px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                      {filters.category.length}
                    </span>
                  )}
                </span>
                {showFilter === "category" && (
                  <div
                    ref={filterRefs.category}
                    className="absolute left-0 top-full z-[99999] mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-2xl"
                    style={{ minWidth: 180 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="p-2">
                      <Select
                        isMulti
                        closeMenuOnSelect={false}
                        options={getFilterOpts("category")}
                        value={getFilterOpts("category").filter((o) =>
                          filters.category.includes(o.value),
                        )}
                        onChange={(opts) =>
                          setFilters((p) => ({
                            ...p,
                            category: opts ? opts.map((o) => o.value) : [],
                          }))
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={customStyles}
                      />
                    </div>
                  </div>
                )}
              </th>

              {/* Assembly - filterable */}
              <th
                className={`px-2 py-2 text-left uppercase tracking-wider cursor-pointer relative min-w-[140px] ${filters.condition.length ? "text-orange-400" : "text-gray-300"}`}
                onClick={(e) => {
                  if (!e.target.closest('[class*="css-"]'))
                    setShowFilter(
                      showFilter === "condition" ? null : "condition",
                    );
                }}
              >
                <span className="flex items-center gap-0.5">
                  Assembly <ChevronDownIcon className="w-3 h-3" />
                  {filters.condition.length > 0 && (
                    <span className="px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                      {filters.condition.length}
                    </span>
                  )}
                </span>
                {showFilter === "condition" && (
                  <div
                    ref={filterRefs.condition}
                    className="absolute left-0 top-full z-[99999] mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-2xl"
                    style={{ minWidth: 260 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="p-2">
                      <Select
                        isMulti
                        closeMenuOnSelect={false}
                        options={getFilterOpts("condition")}
                        value={getFilterOpts("condition").filter((o) =>
                          filters.condition.includes(o.value),
                        )}
                        onChange={(opts) =>
                          setFilters((p) => ({
                            ...p,
                            condition: opts ? opts.map((o) => o.value) : [],
                          }))
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={customStyles}
                      />
                    </div>
                  </div>
                )}
              </th>

              {/* Bid Area - filterable */}
              <th
                className={`px-2 py-2 text-left uppercase tracking-wider cursor-pointer relative min-w-[120px] ${filters.bidArea.length ? "text-orange-400" : "text-gray-300"}`}
                onClick={(e) => {
                  if (!e.target.closest('[class*="css-"]'))
                    setShowFilter(showFilter === "bidArea" ? null : "bidArea");
                }}
              >
                <span className="flex items-center gap-0.5">
                  Bid Area <ChevronDownIcon className="w-3 h-3" />
                  {filters.bidArea.length > 0 && (
                    <span className="px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                      {filters.bidArea.length}
                    </span>
                  )}
                </span>
                {showFilter === "bidArea" && (
                  <div
                    ref={filterRefs.bidArea}
                    className="absolute left-0 top-full z-[99999] mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-2xl"
                    style={{ minWidth: 240 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="p-2">
                      <Select
                        isMulti
                        closeMenuOnSelect={false}
                        options={getFilterOpts("bidArea")}
                        value={getFilterOpts("bidArea").filter((o) =>
                          filters.bidArea.includes(o.value),
                        )}
                        onChange={(opts) =>
                          setFilters((p) => ({
                            ...p,
                            bidArea: opts ? opts.map((o) => o.value) : [],
                          }))
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={customStyles}
                      />
                    </div>
                  </div>
                )}
              </th>

              <th
                className="px-2 py-2 text-right text-gray-300 uppercase tracking-wider cursor-pointer"
                onClick={() => requestSort("deltaQty")}
              >
                Qty Δ
                {getSortIndicator("deltaQty") && (
                  <span className="ml-1 text-orange-400">
                    {getSortIndicator("deltaQty")}
                  </span>
                )}
              </th>
              <th className="px-2 py-2 text-right text-gray-300 uppercase tracking-wider">
                Unit Cost
              </th>
              <th className="px-2 py-2 text-center text-gray-300 uppercase tracking-wider">
                UOM
              </th>
              <th
                className="px-2 py-2 text-right text-gray-300 uppercase tracking-wider cursor-pointer"
                onClick={() => requestSort("amount")}
              >
                Total
                {getSortIndicator("amount") && (
                  <span className="ml-1 text-orange-400">
                    {getSortIndicator("amount")}
                  </span>
                )}
              </th>
              <th className="px-2 py-2 text-center text-gray-300 uppercase tracking-wider w-14">
                Order
              </th>
              {!isLocked && <th className="px-2 py-2 w-8"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-600">
            {sortedItems.map((item) => {
              const isCoverageItem =
                item.category === "M" &&
                (isSFUnit(item.uom) || isLFUnit(item.uom));
              const coverage = parseFloat(item.coveragePerUnit) || 1;
              const pkgCost = isCoverageItem
                ? (item.unitCost || 0) * coverage
                : item.unitCost || 0;
              const isFromBudget =
                !!item.sourceLineItemId || !!item.sourceAssemblyId;
              const isEditable =
                !isLocked &&
                (!isFromBudget || item.isFromDatabase) &&
                item.sourceType !== "co_item";

              return (
                <tr key={item._localId} className="hover:bg-gray-600/40">
                  {/* Description */}
                  <td className="px-2 py-2">
                    <div className="text-gray-200">
                      {item.customerDescription || item.itemDescription || "—"}
                    </div>
                    {item.sourceType === "co_item" && (
                      <span className="text-[10px] text-orange-400/70">
                        CO item delta
                      </span>
                    )}
                    {item.size && isCoverageItem && (
                      <div className="text-[10px] text-gray-500 font-mono">
                        {item.size}
                      </div>
                    )}
                  </td>

                  {/* Category */}
                  <td className="px-2 py-2 text-center">
                    <span
                      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium ${item.category === "M" ? "bg-blue-500/20 text-blue-300" : "bg-purple-500/20 text-purple-300"}`}
                    >
                      {item.category === "M" ? "Mat" : "Lab"}
                    </span>
                  </td>

                  {/* Assembly */}
                  <td className="px-2 py-2">
                    {isEditable ? (
                      <CreatableSelect
                        options={assemblyOptions}
                        value={
                          assemblyOptions.find(
                            (o) => o.value === item.conditionName,
                          ) ||
                          (item.conditionName
                            ? {
                                value: item.conditionName,
                                label: item.conditionName,
                              }
                            : null)
                        }
                        onChange={(opt) =>
                          handleUpdateItem(
                            item._localId,
                            "conditionName",
                            opt?.value || "",
                          )
                        }
                        onCreateOption={(v) =>
                          handleUpdateItem(item._localId, "conditionName", v)
                        }
                        isClearable
                        placeholder="Select or create..."
                        formatCreateLabel={(v) => `New: "${v}"`}
                        className="min-w-[160px]"
                        styles={customStyles}
                        menuPortalTarget={document.body}
                      />
                    ) : (
                      <span className="text-gray-300">
                        {item.conditionName || "—"}
                      </span>
                    )}
                  </td>

                  {/* Bid Area */}
                  <td className="px-2 py-2">
                    {isEditable ? (
                      <CreatableSelect
                        options={bidAreaOptions}
                        value={
                          bidAreaOptions.find(
                            (o) => o.value === item.bidArea,
                          ) ||
                          (item.bidArea
                            ? { value: item.bidArea, label: item.bidArea }
                            : null)
                        }
                        onChange={(opt) =>
                          handleUpdateItem(
                            item._localId,
                            "bidArea",
                            opt?.value || "",
                          )
                        }
                        onCreateOption={(v) =>
                          handleUpdateItem(item._localId, "bidArea", v)
                        }
                        isClearable
                        placeholder="Select or create..."
                        formatCreateLabel={(v) => `New: "${v}"`}
                        className="min-w-[130px]"
                        styles={customStyles}
                        menuPortalTarget={document.body}
                      />
                    ) : (
                      <span className="text-gray-300">
                        {item.bidArea || "—"}
                      </span>
                    )}
                  </td>

                  {/* Qty Δ */}
                  {/* Qty Δ */}
                  <td className="px-2 py-2">
                    <div className="flex flex-col gap-0.5 items-end">
                      <input
                        type="text"
                        inputMode="decimal"
                        disabled={isLocked}
                        value={
                          item._rawDeltaInput !== undefined
                            ? item._rawDeltaInput
                            : (item.deltaQty ?? 0)
                        }
                        onChange={(e) =>
                          handleUpdateItem(
                            item._localId,
                            "deltaQty",
                            e.target.value,
                          )
                        }
                        onBlur={() => {
                          if (item._rawDeltaInput !== undefined)
                            handleUpdateItem(item._localId, "deltaQty", "0");
                        }}
                        className={`w-20 px-2 py-1 text-right rounded border text-sm font-medium ${
                          (item.deltaQty || 0) < 0
                            ? "text-red-400"
                            : "text-gray-200"
                        } ${isLocked ? "bg-gray-700 opacity-60" : "bg-gray-600"} border-gray-500`}
                      />
                      {/* Max negative hint */}

                      {(item.sourceType === "co_item" ||
                        (isFromBudget && !item.sourceType)) && (
                        <span className="text-[9px]">
                          {netLoading && item.sourceType === "co_item" ? (
                            <span className="text-gray-500 italic animate-pulse">
                              loading min...
                            </span>
                          ) : item.budgetedQty > 0 ? (
                            <span className="text-gray-500">
                              min: -{item.budgetedQty}
                            </span>
                          ) : (
                            <span className="text-red-400/70 italic">
                              no deductions remaining
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Unit Cost */}
                  <td className="px-2 py-2 text-right text-gray-300">
                    <div className="flex flex-col items-end">
                      <span>{formatCurrency(item.unitCost || 0)}</span>
                      {isCoverageItem && (
                        <span className="text-[10px] text-orange-300">
                          ×{coverage} = {formatCurrency(pkgCost)}/pc
                        </span>
                      )}
                    </div>
                  </td>

                  {/* UOM */}
                  <td className="px-2 py-2 text-center text-gray-300">
                    {isCoverageItem ? "PC" : item.uom || "EA"}
                  </td>

                  {/* Total */}
                  <td className="px-2 py-2 text-right">
                    <span
                      className={`font-medium ${(item.amount || 0) < 0 ? "text-red-400" : "text-gray-200"}`}
                    >
                      {formatCurrency(item.amount || 0)}
                    </span>
                  </td>

                  {/* Order */}
                  <td className="px-2 py-2 text-center">
                    {!hasFilters && !isLocked ? (
                      <div className="flex flex-col items-center gap-0.5">
                        <button
                          onClick={() => onMove(item._localId, "up")}
                          className="text-gray-400 hover:text-orange-400 disabled:opacity-30"
                          disabled={items.indexOf(item) === 0}
                        >
                          <ChevronUpIcon className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => onMove(item._localId, "down")}
                          className="text-gray-400 hover:text-orange-400 disabled:opacity-30"
                          disabled={items.indexOf(item) === items.length - 1}
                        >
                          <ChevronDownIcon className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <span className="text-[10px] text-gray-600 italic">
                        —
                      </span>
                    )}
                  </td>

                  {/* Delete */}
                  {!isLocked && (
                    <td className="px-2 py-2">
                      <button
                        onClick={() => onRemove(item._localId)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Total row */}
      <div className="mt-3 flex justify-end">
        <div className="bg-gray-800 rounded px-4 py-2 border border-gray-600">
          <span className="text-sm text-gray-400 mr-3">
            Amendment Cost Impact:
          </span>
          <span
            className={`text-lg font-bold ${totalCost >= 0 ? "text-green-400" : "text-red-400"}`}
          >
            {formatCurrency(totalCost)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Single Amendment Editor (the "active" draft being worked on) ─────────────
function AmendmentEditor({
  amendment,
  changeOrderId,
  projectId,
  coItems = [],
  budgetItems = [],
  netQuantities,
  databaseItems = [],
  loadingDatabase = false,
  vendors = [],
  ExcelUploadModal = null,
  token,
  showAlert,
  onSaved,
  onClose,
  fetchNetQuantities,
  fetchDatabaseItems,
}) {
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );
  // Ensure netQuantities is always a Map
  const safeNetQuantities =
    netQuantities instanceof Map ? netQuantities : new Map();

  const [items, setItems] = useState(() =>
    (amendment?.items || []).map((item, i) => ({
      ...item,
      _localId: `amend-${i}-${Date.now()}`,
      // Resolve budgetedQty for CO item deltas from coItems list
      budgetedQty:
        item.budgetedQty ||
        (() => {
          if (item.sourceType === "co_item" && item.coItemId) {
            const coItem = coItems.find(
              (c) => String(c._id) === String(item.coItemId),
            );
            return coItem
              ? Math.abs(coItem.deltaQty || coItem.quantity || 0)
              : 0;
          }
          return 0;
        })(),
    })),
  );

  const [name, setName] = useState(amendment?.name || "");
  const [note, setNote] = useState(amendment?.note || "");
  const [isSaving, setIsSaving] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [showDatabaseModal, setShowDatabaseModal] = useState(false);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [showCOItemsModal, setShowCOItemsModal] = useState(false);
  const [selectedBudgetItems, setSelectedBudgetItems] = useState([]);
  const [selectedDatabaseItems, setSelectedDatabaseItems] = useState([]);

  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [isActioning, setIsActioning] = useState(false);
  const [dbFilters, setDbFilters] = useState({
    category: [],
    uom: [],
    search: "",
  });
  const isLocked =
    amendment?.status === "Approved" || amendment?.status === "Rejected";
  const isDraft = amendment?.status === "Draft";
  const isPending = amendment?.status === "Pending";

  useEffect(() => {
    if (showBudgetModal && safeNetQuantities.size === 0) {
      fetchNetQuantities(true);
    }
  }, [showBudgetModal]);

  useEffect(() => {
    if (safeNetQuantities.size === 0) return;
    setItems((prev) =>
      prev.map((item) => {
        if (item.sourceType !== "co_item") return item;

        // Find the matching CO item to get its createdDeltaItemId
        const coItem = coItems.find(
          (c) => String(c._id) === String(item.coItemId),
        );

        // Use createdDeltaItemId first (assembly line item _id),
        // then sourceLineItemId, then coItemId
        const key =
          coItem?.createdDeltaItemId?.toString() ||
          coItem?.sourceLineItemId?.toString() ||
          item.sourceLineItemId?.toString() ||
          item.coItemId?.toString();

        const netInfo = safeNetQuantities.get(key);
        if (!netInfo) return item;

        const netRemaining = Math.max(0, netInfo.netRemaining ?? 0);
        return {
          ...item,
          budgetedQty: netRemaining,
          netRemaining,
        };
      }),
    );
  }, [safeNetQuantities]);
  useEffect(() => {
    if (showDatabaseModal) {
      if (safeNetQuantities.size === 0) fetchNetQuantities(true);
      if (databaseItems.length === 0) fetchDatabaseItems();
    }
  }, [showDatabaseModal]);

  // ── Item update handler (same logic as CO form) ──────────────────────────
  const handleUpdateItem = useCallback((localId, field, value) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item._localId !== localId) return item;
        const updated = { ...item };

        if (field === "deltaQty" || field === "quantity") {
          const raw = String(value).trim();
          if (raw === "" || raw === "-" || raw === "-." || raw === ".") {
            updated._rawDeltaInput = raw;
            updated.deltaQty = 0;
            updated.quantity = 0;
            updated.amount = 0;
            return updated;
          }
          const parsed = parseFloat(raw);
          if (isNaN(parsed)) return item;
          delete updated._rawDeltaInput;
          let newDelta = item.category === "L" ? parsed : Math.round(parsed);

          // Negative threshold enforcement
          // Negative threshold enforcement
          const budgeted = item.budgetedQty || 0;
          const isFromBudget =
            !!item.sourceLineItemId || item.sourceType === "co_item";
          if (isFromBudget) {
            // Allow any positive qty, but negative is capped at -budgetedQty (which may be 0)
            if (newDelta < -budgeted) newDelta = -budgeted;
          } else {
            if (newDelta < 0) newDelta = 0;
          }
          updated.deltaQty = newDelta;
          updated.quantity = Math.abs(newDelta);
        } else if (field === "conditionName") {
          updated.conditionName = value;
        } else {
          updated[field] = value;
          if (field === "size") {
            const cov = parseSizeToCoverage(value, updated.uom);
            if (cov !== null) updated.coveragePerUnit = String(cov);
          }
        }

        // Recalculate amount
        const qty = Math.abs(updated.deltaQty || 0);
        const coverage = parseFloat(updated.coveragePerUnit) || 1;
        const isCov =
          updated.category === "M" &&
          (isSFUnit(updated.uom) || isLFUnit(updated.uom));
        const baseCost =
          updated.category !== "M" && updated.unitRate > 0
            ? updated.unitRate
            : updated.unitCost || 0;
        const costTotal = isCov ? qty * coverage * baseCost : qty * baseCost;
        const delta = updated.deltaQty || 0;
        updated.amount = delta < 0 ? -costTotal : costTotal;

        return updated;
      }),
    );
  }, []);

  const handleRemoveItem = useCallback((localId) => {
    setItems((prev) => prev.filter((i) => i._localId !== localId));
  }, []);

  const handleMoveItem = useCallback((localId, direction) => {
    setItems((prev) => {
      const arr = [...prev];
      const idx = arr.findIndex((i) => i._localId === localId);
      if (idx < 0) return prev;
      const target = direction === "up" ? idx - 1 : idx + 1;
      if (target < 0 || target >= arr.length) return prev;
      const [removed] = arr.splice(idx, 1);
      arr.splice(target, 0, removed);
      return arr;
    });
  }, []);

  // ── Add from Budget (same logic as CO form) ──────────────────────────────
  const addBudgetItems = () => {
    if (!selectedBudgetItems.length) {
      showAlert("error", "Error", "Please select at least one item");
      return;
    }

    const isDup = (newItem) =>
      items.some((ex) => {
        if (newItem.sourceLineItemId && ex.sourceLineItemId)
          return (
            String(newItem.sourceLineItemId) === String(ex.sourceLineItemId)
          );
        return false;
      });

    const newItems = selectedBudgetItems
      .filter((item) => !isDup(item))
      .map((item, idx) => {
        const itemKey = (
          item.originalLineItemId ||
          item.lineItemId ||
          item._id ||
          item.key
        )?.toString();
        const netInfo = safeNetQuantities.get(itemKey) || item;
        const remaining = netInfo.netRemaining ?? item.remainingQty ?? 0;
        const budgeted =
          netInfo.netRequiredQty ??
          item.budgetedQty ??
          item.requiredQty ??
          remaining;
        const unitCost = Number(
          netInfo.latestPrice ?? item.unitCost ?? item.price ?? 0,
        );
        const coverage =
          netInfo.latestCoveragePerUnit ??
          parseFloat(item.coveragePerUnit) ??
          1;
        const isMat = item.category === "M";
        const isCov = isMat && (isSFUnit(item.uom) || isLFUnit(item.uom));
        const baseAmount = isCov ? 1 * coverage * unitCost : 1 * unitCost;

        return {
          _localId: `amend-budget-${Date.now()}-${idx}`,
          sourceType: "budget",
          sourceAssemblyId: item.assemblyId,
          sourceLineItemId: itemKey,
          isFromBudget: true,
          itemCode: item.itemCode || item.materialCode || item.laborCode || "",
          category: item.category,
          itemDescription:
            item.itemDescription || item.costCodeDescription || "",
          customerDescription:
            item.itemDescription || item.costCodeDescription || "",
          conditionName: item.conditionName || "",
          bidArea: item.bidArea || "",
          deltaQty: 1,
          quantity: 1,
          unitCost,
          unitRate: isMat ? 0 : unitCost,
          size: netInfo.latestSize || item.size || "",
          uom: item.uom || "",
          coveragePerUnit: String(coverage),
          vendor: item.vendor?._id || item.vendor || null,
          forBudget: true,
          remainingQty: remaining,
          budgetedQty: budgeted,
          amount: baseAmount,
        };
      });

    setItems((prev) => [...prev, ...newItems]);
    setSelectedBudgetItems([]);
    setShowBudgetModal(false);
    if (newItems.length)
      showAlert(
        "success",
        "Added",
        `${newItems.length} budget item(s) added to amendment`,
      );
  };

  // ── Add from Database (same logic as CO form) ────────────────────────────
  const addDatabaseItems = () => {
    if (!selectedDatabaseItems.length) {
      showAlert("error", "Error", "Please select at least one item");
      return;
    }

    const newItems = selectedDatabaseItems
      .map((sel, idx) => {
        const item = databaseItems.find((d) => d.itemCode === sel.itemCode);
        if (!item) return null;
        let unitCost = item.defaultCost || 0;
        const vendorToUse =
          sel.selectedVendor ||
          item.defaultVendor?._id ||
          item.defaultVendor ||
          null;
        if (vendorToUse && Array.isArray(item.vendorPrices)) {
          const vp = item.vendorPrices.find(
            (p) => (p.vendor?._id || p.vendor) === vendorToUse,
          );
          if (vp) unitCost = vp.price || 0;
        }
        const qty = Math.abs(sel.deltaQty || 1);
        const uom = item.uom || "";
        const isCov = item.category === "M" && (isSFUnit(uom) || isLFUnit(uom));
        let size = sel.editedSize?.trim() || item.size?.trim() || "";
        let coverage =
          sel.editedCoverage && sel.editedCoverage !== 1
            ? sel.editedCoverage
            : isCov && size
              ? (parseSizeToCoverage(size, uom) ??
                parseFloat(item.coveragePerUnit) ??
                1)
              : parseFloat(item.coveragePerUnit) || 1;
        const amount = isCov
          ? Number((qty * coverage * unitCost).toFixed(2))
          : Number((qty * unitCost).toFixed(2));

        return {
          _localId: `amend-db-${Date.now()}-${idx}`,
          sourceType: "database",
          isFromDatabase: true,
          itemDatabaseId: item._id,
          itemCode: sel.itemCode,
          category: item.category,
          itemDescription: item.description || "",
          customerDescription: item.description || "",
          conditionName: sel.conditionName || "",
          bidArea: sel.bidArea || "",
          deltaQty: qty,
          quantity: qty,
          unitCost,
          unitRate: item.category !== "M" ? unitCost : 0,
          size,
          uom,
          coveragePerUnit: String(coverage),
          vendor: vendorToUse,
          forBudget: true,
          budgetedQty: 0,
          amount,
        };
      })
      .filter(Boolean);

    setItems((prev) => [...prev, ...newItems]);
    setSelectedDatabaseItems([]);
    setShowDatabaseModal(false);
    setDbFilters({ category: [], uom: [], search: "" });
    showAlert("success", "Added", `${newItems.length} item(s) from database`);
  };

  // ── Add from CO Items (delta on existing approved CO budget items) ────────
  const [coItemSearch, setCoItemSearch] = useState("");
  const filteredCoItems = useMemo(() => {
    if (!coItemSearch) return coItems;
    const s = coItemSearch.toLowerCase();
    return coItems.filter(
      (i) =>
        (i.customerDescription || i.itemDescription || "")
          .toLowerCase()
          .includes(s) || (i.conditionName || "").toLowerCase().includes(s),
    );
  }, [coItems, coItemSearch]);

  useEffect(() => {
    if (showCOItemsModal && safeNetQuantities.size === 0) {
      fetchNetQuantities(true);
    }
  }, [showCOItemsModal]);

  useEffect(() => {
    if (safeNetQuantities.size === 0) return;
    setItems((prev) =>
      prev.map((item) => {
        if (item.sourceType !== "co_item") return item;

        const coItem = coItems.find(
          (c) => String(c._id) === String(item.coItemId),
        );

        // Use createdDeltaItemId first (assembly line item _id)
        const key =
          coItem?.createdDeltaItemId?.toString() ||
          coItem?.sourceLineItemId?.toString() ||
          item.sourceLineItemId?.toString() ||
          item.coItemId?.toString();

        const netInfo = safeNetQuantities.get(key);
        if (!netInfo) return item;

        const netRemaining = Math.max(0, netInfo.netRemaining ?? 0);
        return {
          ...item,
          budgetedQty: netRemaining,
          netRemaining,
        };
      }),
    );
  }, [safeNetQuantities]);

  const addCOItem = (coItem) => {
    const alreadyAdded = items.some(
      (i) =>
        i.sourceType === "co_item" && String(i.coItemId) === String(coItem._id),
    );
    if (alreadyAdded) {
      showAlert(
        "warning",
        "Already Added",
        "This CO item is already in this amendment",
      );
      return;
    }

    // Use createdDeltaItemId (assembly line item _id) for net qty lookup
    const netKey =
      coItem.createdDeltaItemId?.toString() ||
      coItem.sourceLineItemId?.toString() ||
      coItem._id?.toString();

    const netInfo = safeNetQuantities.get(netKey);
    const netRemaining = netInfo
      ? (netInfo.netRemaining ??
        Math.abs(coItem.deltaQty || coItem.quantity || 0))
      : Math.abs(coItem.deltaQty || coItem.quantity || 0);

    const originalQty = Math.abs(coItem.deltaQty || coItem.quantity || 0);
    const budgetedQty = Math.max(0, netRemaining);

    setItems((prev) => [
      ...prev,
      {
        _localId: `amend-co-${Date.now()}`,
        sourceType: "co_item",
        coItemId: coItem._id,
        // Pass assembly-level IDs for backend resolution
        sourceLineItemId:
          coItem.createdDeltaItemId || coItem.sourceLineItemId || null,
        sourceAssemblyId:
          coItem.createdInAssemblyId || coItem.sourceAssemblyId || null,
        coItemDescription:
          coItem.customerDescription || coItem.itemDescription || "",
        coItemCode: coItem.itemCode || "",
        coItemConditionName: coItem.conditionName || "",
        coItemBidArea: coItem.bidArea || "",
        coItemCategory: coItem.category || "M",
        coItemUom: coItem.uom || "",
        coItemSize: coItem.size || "",
        coItemCoveragePerUnit: String(coItem.coveragePerUnit || "1"),
        laborId: coItem.laborId || "",
        category: coItem.category || "M",
        customerDescription:
          coItem.customerDescription || coItem.itemDescription || "",
        conditionName: coItem.conditionName || "",
        bidArea: coItem.bidArea || "",
        uom: coItem.uom || "",
        size: coItem.size || "",
        coveragePerUnit: String(coItem.coveragePerUnit || "1"),
        unitCost: Number(coItem.unitCost || 0),
        deltaQty: 1,
        quantity: 1,
        budgetedQty,
        originalQty,
        netRemaining,
        amount: Number(coItem.unitCost || 0),
      },
    ]);
  };

  // ── Excel upload (reuses same addBudgetItemsToChangeOrder style) ──────────
  const addExcelItems = (uploadedItems) => {
    if (!uploadedItems.length) {
      showAlert("error", "Error", "No items to add");
      return;
    }
    const processed = uploadedItems.map((item, idx) => {
      const uom = item.uom || "";
      const isCov = item.category === "M" && (isSFUnit(uom) || isLFUnit(uom));
      let coverage = parseFloat(item.coveragePerUnit) || 1;
      if (isCov && item.size) {
        const parsed = parseSizeToCoverage(item.size, uom);
        if (parsed !== null) coverage = parsed;
      }
      const qty = Math.abs(item.deltaQty || item.quantity || 0);
      const unitCost = item.unitCost || 0;
      const baseAmount = isCov ? qty * coverage * unitCost : qty * unitCost;
      const signedDelta = item.deltaQty || item.quantity || 0;
      return {
        ...item,
        _localId: `amend-excel-${Date.now()}-${idx}`,
        sourceType: "database",
        coveragePerUnit: String(coverage),
        amount: signedDelta < 0 ? -baseAmount : baseAmount,
        budgetedQty: 0,
      };
    });
    setItems((prev) => [...prev, ...processed]);
    showAlert(
      "success",
      "Success",
      `Added ${processed.length} item(s) from Excel`,
    );
  };

  // ── Save to API ───────────────────────────────────────────────────────────
  const handleSave = async () => {
    const missing = items.filter(
      (i) => !i.bidArea?.trim() || !i.conditionName?.trim(),
    );
    if (missing.length) {
      showAlert(
        "error",
        "Missing Fields",
        `${missing.length} item(s) are missing Bid Area or Assembly`,
      );
      return;
    }
    try {
      setIsSaving(true);
      const payload = {
        name: name.trim() || `Amendment ${amendment?.number || ""}`,
        note: note.trim(),
        items: items.map(({ _localId, _rawDeltaInput, ...rest }) => rest),
      };
      await axios.put(
        `${API_BASE}/changeOrders2/${changeOrderId}/amendments/${amendment._id}`,
        payload,
        { headers },
      );
      showAlert("success", "Saved", "Amendment saved successfully");
      onSaved();
    } catch (err) {
      showAlert(
        "error",
        "Error",
        err.response?.data?.message || "Failed to save amendment",
      );
    } finally {
      setIsSaving(false);
    }
  };

  // ── Approve / Reject ──────────────────────────────────────────────────────
  const handleApprove = async () => {
    setShowApproveConfirm(false);
    try {
      setIsActioning(true);
      await handleSave(); // save first
      await axios.put(
        `${API_BASE}/changeOrders2/${changeOrderId}/amendments/${amendment._id}/approve`,
        {},
        { headers },
      );
      showAlert(
        "success",
        "Approved",
        "Amendment approved and applied to variance budget",
      );
      onSaved();
      onClose();
    } catch (err) {
      showAlert(
        "error",
        "Error",
        err.response?.data?.message || "Failed to approve amendment",
      );
    } finally {
      setIsActioning(false);
    }
  };

  const handleReject = async () => {
    setShowRejectConfirm(false);
    try {
      setIsActioning(true);
      await axios.put(
        `${API_BASE}/changeOrders2/${changeOrderId}/amendments/${amendment._id}/reject`,
        {},
        { headers },
      );
      showAlert("success", "Rejected", "Amendment rejected");
      onSaved();
      onClose();
    } catch (err) {
      showAlert(
        "error",
        "Error",
        err.response?.data?.message || "Failed to reject amendment",
      );
    } finally {
      setIsActioning(false);
    }
  };

  const statusColor =
    {
      Draft: "bg-gray-600 text-gray-200",
      Pending: "bg-yellow-600 text-white",
      Approved: "bg-green-600 text-white",
      Rejected: "bg-red-600 text-white",
    }[amendment?.status] || "bg-gray-600 text-gray-200";

  return (
    <div className="space-y-4">
      <ConfirmModal
        open={showApproveConfirm}
        onClose={() => setShowApproveConfirm(false)}
        onConfirm={handleApprove}
        title="Approve Amendment"
        message="This will save and apply all items to the variance budget. This cannot be undone."
        confirmText="Approve"
        confirmColor="green"
      />
      <ConfirmModal
        open={showRejectConfirm}
        onClose={() => setShowRejectConfirm(false)}
        onConfirm={handleReject}
        title="Reject Amendment"
        message="Are you sure you want to reject this amendment?"
        confirmText="Reject"
        confirmColor="red"
      />

      {/* CO Items picker modal */}
      {showCOItemsModal && (
        <div className="fixed inset-0 bg-gray-900/75 flex items-center justify-center z-50">
          <div className="bg-gray-700 rounded-lg shadow-xl w-full max-w-2xl border border-gray-600 max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-gray-600">
              <h3 className="text-lg font-semibold text-white">
                Add Delta on Existing CO Item
              </h3>
              <button
                onClick={() => setShowCOItemsModal(false)}
                className="text-gray-400 hover:text-gray-200"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="p-3 border-b border-gray-600">
              <input
                type="text"
                value={coItemSearch}
                onChange={(e) => setCoItemSearch(e.target.value)}
                placeholder="Search CO items..."
                className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded text-gray-200 text-sm"
              />
            </div>
            <div className="overflow-y-auto flex-1">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-800 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-gray-300">
                      Description
                    </th>
                    <th className="px-3 py-2 text-center text-gray-300">Cat</th>
                    <th className="px-3 py-2 text-left text-gray-300">
                      Assembly
                    </th>
                    <th className="px-3 py-2 text-right text-gray-300">
                      Orig Qty
                    </th>
                    <th className="px-3 py-2 text-right text-gray-300">
                      Unit Cost
                    </th>
                    <th className="px-3 py-2 text-right text-gray-300">
                      Net Remaining
                    </th>
                    <th className="px-3 py-2 w-20"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-600">
                  {filteredCoItems.map((coItem) => {
                    const alreadyAdded = items.some(
                      (i) =>
                        i.sourceType === "co_item" &&
                        String(i.coItemId) === String(coItem._id),
                    );

                    // Get net info for display
                    const key =
                      coItem.sourceLineItemId?.toString() ||
                      coItem._id?.toString();
                    const netInfo = safeNetQuantities.get(key);
                    const netRemaining = netInfo
                      ? (netInfo.netRemaining ??
                        Math.abs(coItem.deltaQty || coItem.quantity || 0))
                      : Math.abs(coItem.deltaQty || coItem.quantity || 0);
                    const remainingColor =
                      netRemaining <= 0
                        ? "text-red-400"
                        : netRemaining < 5
                          ? "text-yellow-400"
                          : "text-green-400";

                    return (
                      <tr
                        key={coItem._id}
                        className={`hover:bg-gray-600/40 ${alreadyAdded ? "opacity-50" : ""}`}
                      >
                        <td className="px-3 py-2 text-gray-200">
                          {coItem.customerDescription ||
                            coItem.itemDescription ||
                            "—"}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span
                            className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${coItem.category === "M" ? "bg-blue-500/20 text-blue-300" : "bg-purple-500/20 text-purple-300"}`}
                          >
                            {coItem.category === "M" ? "Mat" : "Lab"}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-300">
                          {coItem.conditionName || "—"}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-300">
                          {coItem.deltaQty || coItem.quantity || 0}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-300">
                          {formatCurrency(coItem.unitCost || 0)}
                        </td>
                        {/* Net Remaining column */}
                        {/* Net Remaining column */}
                        <td className="px-3 py-2 text-right">
                          {safeNetQuantities.size === 0 ? (
                            <span className="text-gray-500 text-xs italic">
                              loading...
                            </span>
                          ) : (
                            <div className="flex flex-col items-end">
                              <span
                                className={`text-sm font-medium ${remainingColor}`}
                              >
                                {netRemaining.toFixed(2)}
                              </span>
                              {netRemaining <= 0 ? (
                                <span className="text-[10px] text-red-400/70 italic">
                                  no deductions allowed
                                </span>
                              ) : (
                                <span className="text-[10px] text-gray-500">
                                  max deduct: -{netRemaining.toFixed(2)}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => {
                              addCOItem(coItem);
                              setShowCOItemsModal(false);
                            }}
                            disabled={alreadyAdded}
                            className={`px-2 py-1 text-xs rounded ${
                              alreadyAdded
                                ? "bg-gray-600 text-gray-500 cursor-not-allowed"
                                : "bg-orange-500 hover:bg-orange-600 text-white"
                            }`}
                          >
                            {alreadyAdded ? "Added" : "Add"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Real Budget Modal */}
      <BudgetItemsModal
        open={showBudgetModal}
        onClose={() => {
          setShowBudgetModal(false);
          setSelectedBudgetItems([]);
        }}
        budgetItems={budgetItems}
        loading={false}
        netLoading={safeNetQuantities.size === 0}
        netQuantities={safeNetQuantities}
        selectedItems={selectedBudgetItems}
        setSelectedItems={setSelectedBudgetItems}
        onAddItems={addBudgetItems}
        formatCurrency={formatCurrency}
        getFilterOptions={(field) => {
          const unique = new Set();
          budgetItems.forEach((item) => {
            let value;
            if (field === "category") value = item.category;
            if (field === "vendor") value = item.vendor?._id || item.vendor;
            if (field === "bidArea") value = item.bidArea;
            if (field === "condition") {
              const raw = item.conditionName;
              if (raw) {
                const str = String(raw).trim();
                const match = str.match(/^\d+\s*[-–—]\s*(.+)$/);
                value = match ? match[1].trim() : str;
              }
            }
            if (field === "uom") value = item.uom || item.container;
            if (value) unique.add(value);
          });
          return Array.from(unique)
            .sort()
            .map((v) => ({ value: v, label: v || "N/A" }));
        }}
      />

      {/* Real Database Modal */}
      <DatabaseItemsModal
        open={showDatabaseModal}
        onClose={() => {
          setShowDatabaseModal(false);
          setSelectedDatabaseItems([]);
          setDbFilters({ category: [], uom: [], search: "" });
        }}
        databaseItems={databaseItems}
        loading={loadingDatabase}
        databaseFilters={dbFilters}
        setDatabaseFilters={setDbFilters}
        selectedDatabaseItems={selectedDatabaseItems}
        setSelectedDatabaseItems={setSelectedDatabaseItems}
        onAddItems={addDatabaseItems}
        formatCurrency={formatCurrency}
        formData={{ defaultMarkupPercentage: 30 }}
        parseSizeToCoverage={parseSizeToCoverage}
        isSFUnit={isSFUnit}
        isLFUnit={isLFUnit}
        getDatabaseFilterOptions={(field) => {
          const unique = new Set();
          databaseItems.forEach((item) => {
            const value = field === "uom" ? item.uom : item[field];
            if (value) unique.add(value);
          });
          return Array.from(unique)
            .sort()
            .map((v) => ({ value: v, label: v }));
        }}
      />

      {/* Excel Upload Modal — passed in as prop so we can reuse the existing one */}
      {showExcelModal && ExcelUploadModal && (
        <ExcelUploadModal
          open={showExcelModal}
          onClose={() => setShowExcelModal(false)}
          onAddItems={addExcelItems}
          databaseItems={databaseItems || []}
          vendors={vendors}
          loadingDatabase={loadingDatabase}
          existingChangeItems={items}
          budgetItems={budgetItems || []}
          netQuantities={
            netQuantities instanceof Map ? netQuantities : new Map()
          }
          projectId={projectId}
        />
      )}

      {/* Amendment header */}
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-600">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <span
              className={`px-2 py-1 rounded text-xs font-medium ${statusColor}`}
            >
              {amendment?.status || "Draft"}
            </span>
            <h3 className="text-white font-semibold">
              Amendment #{amendment?.number || "—"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-200"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isLocked}
              placeholder="e.g., Scope change A"
              className="w-full px-3 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded text-gray-200 disabled:opacity-60"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Note</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={isLocked}
              placeholder="Optional note..."
              className="w-full px-3 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded text-gray-200 disabled:opacity-60"
            />
          </div>
        </div>
      </div>

      {/* Action buttons */}
      {!isLocked && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowCOItemsModal(true)}
            className="px-3 py-1.5 text-sm text-white bg-orange-500 rounded hover:bg-orange-600 flex items-center gap-1"
          >
            <PlusIcon className="h-4 w-4" /> CO Item Delta
          </button>
          <button
            onClick={() => setShowBudgetModal(true)}
            className="px-3 py-1.5 text-sm text-white bg-orange-400 rounded hover:bg-orange-500 flex items-center gap-1"
          >
            <PlusIcon className="h-4 w-4" /> From Budget
          </button>
          <button
            onClick={() => setShowDatabaseModal(true)}
            className="px-3 py-1.5 text-sm text-white bg-blue-500 rounded hover:bg-blue-600 flex items-center gap-1"
          >
            <PlusIcon className="h-4 w-4" /> From Database
          </button>
          {ExcelUploadModal && (
            <button
              onClick={() => setShowExcelModal(true)}
              className="px-3 py-1.5 text-sm text-white bg-purple-500 rounded hover:bg-purple-600 flex items-center gap-1"
            >
              <ArrowUpTrayIcon className="h-4 w-4" /> Upload Excel
            </button>
          )}
        </div>
      )}

      {/* Item table */}
      <AmendmentItemTable
        items={items}
        onUpdate={handleUpdateItem}
        onRemove={handleRemoveItem}
        onMove={handleMoveItem}
        isLocked={isLocked}
        budgetItems={budgetItems || []}
        netQuantities={netQuantities instanceof Map ? netQuantities : new Map()}
        coItems={coItems || []}
        netLoading={safeNetQuantities.size === 0}
      />

      {/* Bottom action bar */}
      {!isLocked && (
        <div className="flex justify-between items-center pt-3 border-t border-gray-600">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-600 text-gray-200 rounded hover:bg-gray-500"
          >
            Close
          </button>
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={isSaving || isActioning}
              className="px-4 py-2 text-sm bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50"
            >
              {isSaving ? "Saving..." : "Save Draft"}
            </button>
            {(isDraft || isPending) && (
              <>
                <button
                  onClick={() => setShowApproveConfirm(true)}
                  disabled={isSaving || isActioning || items.length === 0}
                  className="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                >
                  <CheckCircleIcon className="h-4 w-4" /> Approve
                </button>
                <button
                  onClick={() => setShowRejectConfirm(true)}
                  disabled={isSaving || isActioning}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-1"
                >
                  <XCircleIcon className="h-4 w-4" /> Reject
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {isLocked && (
        <div className="flex justify-end pt-3 border-t border-gray-600">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-600 text-gray-200 rounded hover:bg-gray-500"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Main COAmendmentPanel ────────────────────────────────────────────────────
export default function COAmendmentPanel({
  changeOrder,
  amendments,
  coItems,
  netQtyMap, // plain object version of netQuantities Map
  token,
  showAlert,
  onAmendmentsChange,
  projectId,
  budgetItems = [],
  netQuantities, // the Map version
  databaseItems = [],
  loadingDatabase = false,
  vendors = [],
  ExcelUploadModal = null,
  fetchDatabaseItems,
  fetchNetQuantities,
}) {
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );
  const [activeAmendmentId, setActiveAmendmentId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newAmendName, setNewAmendName] = useState("");
  const [newAmendNote, setNewAmendNote] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    try {
      setIsCreating(true);
      const res = await axios.post(
        `${API_BASE}/changeOrders2/${changeOrder._id}/amendments`,
        {
          name:
            newAmendName.trim() || `Amendment ${(amendments?.length || 0) + 1}`,
          note: newAmendNote.trim(),
        },
        { headers },
      );
      const updated = res.data.changeOrder?.amendments || [
        ...(amendments || []),
        res.data.amendment,
      ];
      onAmendmentsChange(updated);
      setActiveAmendmentId(
        res.data.amendment?._id || updated[updated.length - 1]?._id,
      );
      setShowCreateModal(false);
      setNewAmendName("");
      setNewAmendNote("");
    } catch (err) {
      showAlert(
        "error",
        "Error",
        err.response?.data?.message || "Failed to create amendment",
      );
    } finally {
      setIsCreating(false);
    }
  };

  const handleSaved = async () => {
    try {
      const res = await axios.get(
        `${API_BASE}/changeOrders2/${changeOrder._id}`,
        { headers },
      );
      const updated = res.data.changeOrder?.amendments || amendments;
      onAmendmentsChange(updated);
    } catch {
      // silent
    }
  };

  const activeAmendment = amendments?.find(
    (a) => String(a._id) === String(activeAmendmentId),
  );

  const pendingCount =
    amendments?.filter((a) => a.status === "Pending").length || 0;
  const approvedCount =
    amendments?.filter((a) => a.status === "Approved").length || 0;

  // Net approved impact
  const netImpact = useMemo(() => {
    return (amendments || [])
      .filter((a) => a.status === "Approved")
      .reduce(
        (sum, a) =>
          sum +
          (a.items || []).reduce((s, i) => {
            // amount already has the correct sign
            // negative deltaQty → negative amount
            // positive deltaQty → positive amount
            return s + (Number(i.amount) || 0);
          }, 0),
        0,
      );
  }, [amendments]);
  const statusColor = {
    Draft: "bg-gray-600 text-gray-200",
    Pending: "bg-yellow-600 text-white",
    Approved: "bg-green-600 text-white",
    Rejected: "bg-red-600/80 text-white",
  };

  return (
    <div className="space-y-4">
      <ConfirmModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onConfirm={handleCreate}
        title="New Amendment"
        message={
          <div className="space-y-3 mt-2">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Name</label>
              <input
                type="text"
                value={newAmendName}
                onChange={(e) => setNewAmendName(e.target.value)}
                placeholder={`Amendment ${(amendments?.length || 0) + 1}`}
                className="w-full px-3 py-1.5 text-sm bg-gray-600 border border-gray-500 rounded text-gray-200"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                Note (optional)
              </label>
              <input
                type="text"
                value={newAmendNote}
                onChange={(e) => setNewAmendNote(e.target.value)}
                placeholder="Description of this amendment..."
                className="w-full px-3 py-1.5 text-sm bg-gray-600 border border-gray-500 rounded text-gray-200"
              />
            </div>
          </div>
        }
        confirmText={isCreating ? "Creating..." : "Create"}
        confirmColor="orange"
      />

      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-gray-700 rounded-lg p-3 border border-gray-600">
          <div className="text-xs text-gray-400">Total Amendments</div>
          <div className="text-2xl font-bold text-white">
            {amendments?.length || 0}
          </div>
        </div>
        <div className="bg-gray-700 rounded-lg p-3 border border-gray-600">
          <div className="text-xs text-gray-400">Pending Approval</div>
          <div
            className={`text-2xl font-bold ${pendingCount > 0 ? "text-yellow-400" : "text-white"}`}
          >
            {pendingCount}
          </div>
        </div>
        <div className="bg-gray-700 rounded-lg p-3 border border-gray-600">
          <div className="text-xs text-gray-400">Net Approved Impact</div>
          <div
            className={`text-2xl font-bold ${netImpact >= 0 ? "text-green-400" : "text-red-400"}`}
          >
            {formatCurrency(netImpact)}
          </div>
        </div>
      </div>

      {/* Amendment editor (shown when one is active) */}
      {activeAmendment ? (
        <div className="bg-gray-700 rounded-lg p-4 border border-orange-500/40">
          <AmendmentEditor
            amendment={activeAmendment}
            changeOrderId={changeOrder._id}
            projectId={projectId}
            coItems={coItems || []}
            budgetItems={budgetItems || []}
            netQuantities={
              netQuantities instanceof Map ? netQuantities : new Map()
            }
            databaseItems={databaseItems || []}
            loadingDatabase={loadingDatabase}
            vendors={vendors || []}
            ExcelUploadModal={ExcelUploadModal}
            token={token}
            showAlert={showAlert}
            onSaved={handleSaved}
            onClose={() => setActiveAmendmentId(null)}
            fetchNetQuantities={fetchNetQuantities}
            fetchDatabaseItems={fetchDatabaseItems}
          />
        </div>
      ) : (
        /* Amendment list */
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <h3 className="text-white font-medium">Amendments</h3>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-3 py-1.5 text-sm text-white bg-orange-500 rounded hover:bg-orange-600 flex items-center gap-1"
            >
              <PlusIcon className="h-4 w-4" /> New Amendment
            </button>
          </div>

          {!amendments || amendments.length === 0 ? (
            <div className="text-center py-12 text-gray-400 border-2 border-dashed border-gray-600 rounded-lg">
              <p className="text-sm">No amendments yet.</p>
              <p className="text-xs mt-1">
                Create one to add post-approval scope changes.
              </p>
            </div>
          ) : (
            amendments.map((amendment) => {
              const itemCount = amendment.items?.length || 0;
              const impact = (amendment.items || []).reduce((s, i) => {
                const v = Math.abs(Number(i.amount) || 0);
                return s + ((Number(i.deltaQty) || 0) < 0 ? -v : v);
              }, 0);
              const sc = statusColor[amendment.status] || statusColor.Draft;
              return (
                <div
                  key={amendment._id}
                  className="bg-gray-800 rounded-lg p-3 border border-gray-600 hover:border-gray-500 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${sc}`}
                      >
                        {amendment.status}
                      </span>
                      <div>
                        <span className="text-white font-medium text-sm">
                          #{amendment.number} — {amendment.name || "Unnamed"}
                        </span>
                        {amendment.note && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            {amendment.note}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-xs text-gray-500">
                          {itemCount} item{itemCount !== 1 ? "s" : ""}
                        </div>
                        <div
                          className={`text-sm font-semibold ${impact >= 0 ? "text-green-400" : "text-red-400"}`}
                        >
                          {formatCurrency(impact)}
                        </div>
                      </div>
                      <button
                        onClick={() =>
                          setActiveAmendmentId(String(amendment._id))
                        }
                        className={`px-3 py-1.5 text-xs rounded flex items-center gap-1 ${
                          amendment.status === "Approved" ||
                          amendment.status === "Rejected"
                            ? "bg-gray-600 text-gray-300 hover:bg-gray-500"
                            : "bg-orange-500 text-white hover:bg-orange-600"
                        }`}
                      >
                        <PencilSquareIcon className="h-3.5 w-3.5" />
                        {amendment.status === "Approved" ||
                        amendment.status === "Rejected"
                          ? "View"
                          : "Edit"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
